/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router";
import { router } from "./router";
import { LogtoProvider, type LogtoConfig } from "@logto/react";
import { LOGTO_APPID, LOGTO_RESOURCE, LogtoPermission } from "@src/utils/constants";
import { App, ConfigProvider, theme } from "antd";
import zhCN from "antd/locale/zh_CN";
import { CloseOutlined } from "@ant-design/icons";


const elem = document.getElementById("root")!;

const config: LogtoConfig = {
  endpoint: 'https://logto.ksr.la/',
  appId: LOGTO_APPID,
  resources: [LOGTO_RESOURCE],
  scopes: Object.values(LogtoPermission),
};

const CustomThemeApp = () => {
  // const configProps = useCartoonTheme();
  return (
    <ConfigProvider
      theme={{
        algorithm: theme.darkAlgorithm,
        token: {
          colorPrimary: '#f5c451',
          colorInfo: '#7aa2ff',
          colorSuccess: '#53d6a2',
          colorBgBase: '#0b0e14',
          colorBgContainer: '#141922',
          colorBorder: '#293141',
          borderRadius: 10,
          borderRadiusLG: 16,
          fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          controlHeight: 38,
        },
        components: {
          Card: { headerBg: 'transparent' },
          Layout: { siderBg: '#0f131b' },
          Menu: { darkItemBg: 'transparent', itemBg: 'transparent', itemSelectedBg: '#29271d', itemSelectedColor: '#f5c451', itemBorderRadius: 10 },
          Table: { headerBg: '#191f2a', rowHoverBg: '#1b2230' },
        }
      }}
      drawer={{ closeIcon: <CloseOutlined /> }}
      locale={zhCN}
      form={{ validateMessages: { required: "${label}是必选字段" }}}
    >
      <App>
        <RouterProvider router={router} />
      </App>
    </ConfigProvider>
  );
}

const app = (
  <StrictMode>
    <LogtoProvider config={config}>
      <CustomThemeApp />
    </LogtoProvider>
  </StrictMode>
);

if (import.meta.hot) {
  // With hot module reloading, `import.meta.hot.data` is persisted.
  const root = (import.meta.hot.data.root ??= createRoot(elem));
  root.render(app);
} else {
  // The hot module reloading API is not available in production.
  createRoot(elem).render(app);
}
